<template>
  <div class="selection-shell">
    <v-btn style="position: fixed; top: 10px; z-index: 5; left: 10px" x-large icon @click="$router.push('/')">
      <v-icon color="white">mdi-chevron-left</v-icon>
    </v-btn>

    <v-container>
      <div class="header-section text-center">
        <h1 class="headline mb-2">Wähle dein Erlebnis</h1>
        <p class="sub-headline">Wofür möchtest du dich anmelden?</p>
      </div>

      <!-- Termine zuerst, nur anzeigen wenn welche vorhanden sind oder noch geladen wird -->
      <div class="upcoming-section" v-if="isLoadingReservations || myReservations.length > 0">
        <h2 class="section-headline">Deine nächsten Termine</h2>
        <div v-if="isLoadingReservations" class="text-center py-8">
          <v-progress-circular indeterminate color="var(--v-primary)" size="40"></v-progress-circular>
        </div>
        <div v-else class="reservations-list">
          <v-card v-for="rez in myReservations" :key="rez._id" class="reservation-card" :class="{ 'is-cancelled': rez.isCancelled }">
            <div class="rez-icon">
              <v-icon :color="rez.isCancelled ? 'red' : 'var(--v-primary)'">
                {{ rez.isCancelled ? "mdi-calendar-remove-outline" : "mdi-calendar-check" }}
              </v-icon>
            </div>
            <div class="rez-details">
              <span class="rez-title">{{ rez.reservationType.name }}</span>
              <span class="rez-time">{{ formatDateTime(rez.startTime) }}</span>
              <v-chip v-if="rez.isCancelled" x-small color="red" class="mt-1"> Storniert (Defekt) </v-chip>
            </div>
            <v-spacer></v-spacer>
            <v-btn v-if="!rez.isCancelled" icon @click="promptCancelReservation(rez)" class="cancel-btn" title="Termin stornieren">
              <v-icon>mdi-trash-can-outline</v-icon>
            </v-btn>
          </v-card>
        </div>
      </div>

      <!-- Trenner zwischen Terminen und Auswahl, nur wenn Termine vorhanden -->
      <div v-if="!isLoadingReservations && myReservations.length > 0" class="section-divider">
        <h2 class="section-headline">Neuen Termin buchen</h2>
      </div>

      <div v-if="isLoadingTypes" class="text-center" style="padding: 80px 0">
        <v-progress-circular indeterminate color="var(--v-primary)" size="64"></v-progress-circular>
      </div>

      <div v-else class="type-feed">
        <v-card v-for="type in reservationTypes" :key="type._id" class="type-card" @click="selectType(type)">
          <div class="card-content">
            <div class="card-header">
              <h2 class="card-title">{{ type.name }}</h2>
              <v-chip small class="duration-chip" color="rgba(255,255,255,0.1)">
                <v-icon left small>mdi-clock-outline</v-icon>
                {{ type.timeslotDuration }} min
              </v-chip>
            </div>
            <p class="card-description">{{ type.description }}</p>
          </div>
          <div class="card-action">
            <span>Termin auswählen</span>
            <v-icon right>mdi-arrow-right</v-icon>
          </div>
        </v-card>
        <div v-if="reservationTypes.length === 0" class="no-reservations-placeholder">
          <v-icon class="placeholder-icon">mdi-calendar-search</v-icon>
          <p>Keine Reservierungsmöglichkeiten vorhanden</p>
        </div>
      </div>

      <!-- Placeholder für "keine Termine" ganz nach unten verschoben, falls Auswahl vorhanden -->
      <div
        v-if="!isLoadingReservations && myReservations.length === 0 && !isLoadingTypes && reservationTypes.length > 0"
        class="upcoming-section-empty">
        <div class="no-reservations-placeholder">
          <v-icon class="placeholder-icon">mdi-calendar-search</v-icon>
          <p>Du hast noch keine Termine gebucht.</p>
        </div>
      </div>
    </v-container>
    <ActionDialog
      :show="showDialog"
      :title="dialog.title"
      :text="dialog.text"
      :confirm-button-text="dialog.confirmText"
      :icon="dialog.icon"
      @close="showDialog = false"
      @confirm="handleDialogConfirm" />
    <upgrade-contract
      :useCustomer="true"
      @hide="showContractDialog = false"
      @created="upgradeCreatedDialog = true"
      :dialog="showContractDialog"></upgrade-contract>
    <v-dialog persistent max-width="500" v-model="upgradeCreatedDialog">
      <v-card dark>
        <v-card-title class="primary white--text">Zusatzpaket Upgrade</v-card-title>
        <v-card-text class="mt-5"
          >Vielen Dank für dein Zusatzpaket Upgrade!
          <br />
          Sobald die Aktivierung abgeschlossen ist, erhältst du eine Bestätigung per E-Mail sowie die entsprechende Rechnung.
          <br /><br />
          Bei Fragen zum Vertrag erreichst du uns unter {{ selectedStudio?.telefon || "" }} oder schreibe uns eine Email an
          {{ selectedStudio?.email || "info@wellcomefit.ch" }}.
          <br />
          <br />
          Bis bald im {{ selectedStudio?.art?.replace("AG", "") || "well come FIT" }}
        </v-card-text>
        <v-card-actions class="d-flex justify-center">
          <v-btn text @click="upgradeCreatedDialog = false">Alles klar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import { mapState, mapGetters } from "vuex"
import ActionDialog from "../components/ActionDialog.vue"
import UpgradeContract from "@/views/dashboard/contract/components/upgradeContract.vue"
export default {
  components: {
    ActionDialog,
    UpgradeContract,
  },
  data() {
    return {
      userService,

      showContractDialog: false,
      upgradeCreatedDialog: false,
      reservationTypes: [],
      myReservations: [],

      isLoadingTypes: true,
      isLoadingReservations: true,

      showDialog: false,
      dialog: {
        title: "",
        text: "",
        confirmText: "",
        action: null,
        icon: "mdi-lock-alert-outline",
        cancellationId: null,
      },
      isCancelling: false,
    }
  },
  async created() {
    this.fetchReservationTypes()
    this.fetchMyReservations()
  },
  computed: {
    ...mapGetters("userStore", ["activeContractDetails"]),
    ...mapState("studioStore", ["selectedStudio"]),
  },
  methods: {
    promptCancelReservation(reservation) {
      this.dialog = {
        title: "Termin stornieren?",
        text: `Möchtest du den Termin für <strong>${reservation.reservationType.name}</strong> am<br>${this.formatDateTime(
          reservation.startTime,
        )} wirklich stornieren?`,
        confirmText: "Ja, stornieren",
        confirmButtonColor: "red",
        action: "confirmCancel",
        icon: "mdi-trash-can-outline",
        cancellationId: reservation._id,
      }
      this.showDialog = true
    },
    async executeCancellation() {
      if (!this.dialog.cancellationId) return
      this.isCancelling = true

      try {
        await api.reservationTool.deleteReservation(this.dialog.cancellationId)
        this.myReservations = this.myReservations.filter((rez) => rez._id !== this.dialog.cancellationId)
        this.$toast.success("Termin erfolgreich storniert!")
      } catch (error) {
        console.error("Fehler bei der Stornierung:", error)
      } finally {
        this.isCancelling = false
        this.dialog.cancellationId = null
      }
    },
    async fetchReservationTypes() {
      this.isLoadingTypes = true
      try {
        const response = await api.reservationTool.getReservationTypes(this.selectedStudio?._id)
        this.reservationTypes = response.data.filter((type) => type.active)
      } catch (error) {
        console.error("Fehler beim Laden der Reservierungstypen:", error)
      } finally {
        this.isLoadingTypes = false
      }
    },
    async fetchMyReservations() {
      this.isLoadingReservations = true
      try {
        const response = await api.reservationTool.getMyReservations(this.userId)
        this.myReservations = response.data
      } catch (error) {
        console.error("Fehler beim Laden der eigenen Reservierungen:", error)
      } finally {
        this.isLoadingReservations = false
      }
    },
    selectType(type) {
      if (!type.membersOnly) {
        this.navigateToBooking(type)
        return
      }

      if (!this.activeContractDetails) {
        this.showSubscriptionNeededDialog()
        return
      }

      const requiredSubs = (type.requiredSubscriptionTypes || []).map((s) => s.toLowerCase())
      const requiredAddons = (type.requiredAddonPackages || []).map((p) => p.toLowerCase())

      if (requiredSubs.length === 0 && requiredAddons.length === 0) {
        this.navigateToBooking(type)
        return
      }

      const userSubscriptionTitle = (this.activeContractDetails.title || "").toLowerCase()
      const userActiveAddons = this.activeContractDetails.activeAddonsTitles.map((e) => e.toLowerCase()) || []

      if (
        userSubscriptionTitle.includes("mitarbeiter") ||
        ((userSubscriptionTitle.includes("erwachsene") ||
          userSubscriptionTitle.includes("flexi winti") ||
          userSubscriptionTitle.includes("ahv") ||
          (userSubscriptionTitle.includes("jahres abo") && !userSubscriptionTitle.includes("kombi"))) &&
          !requiredSubs.some((reqSub) => reqSub.includes("pelvi"))) ||
        (userSubscriptionTitle.includes("premium") && !requiredSubs.some((reqSub) => reqSub.includes("pelvi"))) ||
        (userSubscriptionTitle.includes("vip") && !requiredSubs.some((reqSub) => reqSub.includes("pelvi")))
      ) {
        this.navigateToBooking(type)
        return
      }

      let hasPermission = false

      if (requiredSubs.length > 0) {
        hasPermission = requiredSubs.some((reqSub) => userSubscriptionTitle.includes(reqSub))
      }

      if (!hasPermission && requiredAddons.length > 0) {
        hasPermission = requiredAddons.some((reqAddon) => userActiveAddons.some((userAddon) => userAddon.toLowerCase().includes(reqAddon)))
      }

      if (hasPermission) {
        this.navigateToBooking(type)
      } else {
        this.showUpgradeNeededDialog()
      }
    },

    showSubscriptionNeededDialog() {
      this.dialog = {
        title: "Aktives Abo benötigt",
        text: "Für diese Buchung wird ein aktives Abo benötigt.<br>Bitte löse zuerst ein Abo, um fortzufahren.",
        confirmText: "Abo lösen",
        action: "navigateToContracts",
        icon: "mdi-star-circle-outline",
      }
      this.showDialog = true
    },

    showUpgradeNeededDialog() {
      this.dialog = {
        title: "Zusatzpaket erforderlich",
        text: "Für diese Buchung wird ein Zusatzpaket benötigt.<br>Bitte löse zuerst das Zusatzpaket, um fortzufahren.",
        confirmText: "zu den Upgrades",
        action: "navigateToUpgrades",
        icon: "mdi-arrow-up-bold-circle-outline",
      }
      this.showDialog = true
    },

    handleDialogConfirm() {
      if (this.dialog.action === "navigateToContracts") {
        this.$router.push("/contract?studio=" + this.selectedStudio?.code)
      } else if (this.dialog.action === "navigateToUpgrades") {
        this.showContractDialog = true
      } else if (this.dialog.action === "confirmCancel") {
        this.executeCancellation()
      }

      this.showDialog = false
    },

    navigateToBooking(type) {
      this.$router.push({
        name: "ReservationBooking",
        params: { reservationTypeId: type._id },
      })
    },
    formatDateTime(dateTime) {
      return this.$moment.utc(dateTime).format("dd, DD.MM.YY [um] HH:mm [Uhr]")
    },
  },
}
</script>

<style scoped>
.reservation-card {
  display: flex;
  align-items: center;
  padding: 16px;
  background: var(--card-bg) !important;
  border: 1px solid var(--border) !important;
  border-radius: 12px !important;
}

.cancel-btn {
  color: var(--text-secondary) !important;
}

.cancel-btn:hover {
  color: #ff5252 !important;
}
.selection-shell {
  --primary: #ff0055;
  --bg: #121212;
  --card-bg: #1e1e1e;
  --text-primary: #ffffff;
  --text-secondary: #b0b0b0;
  --border: #2c2c2c;
  border-radius: 0px !important;
  background-color: var(--bg);
  min-height: 100vh;
  padding-top: 40px;
  padding-bottom: 40px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

/* --- TERMINE OBEN --- */
.upcoming-section {
  max-width: 600px;
  margin: 0 auto 40px auto;
}
.upcoming-section-empty {
  max-width: 600px;
  margin: 40px auto 0 auto;
}
.section-divider {
  max-width: 600px;
  margin: 0 auto 20px auto;
}
.section-headline {
  font-size: 1.5rem;
  font-weight: 1000;
  text-transform: uppercase;
  font-style: italic;
  color: var(--text-primary);
  margin-bottom: 20px;
  text-align: center;
}
.reservations-list {
  display: grid;
  gap: 12px;
}
.reservation-card {
  display: flex;
  align-items: center;
  padding: 16px;
  background: var(--card-bg) !important;
  border: 1px solid var(--border) !important;
  border-radius: 12px !important;
}
.rez-icon {
  margin-right: 16px;
}
.rez-details {
  display: flex;
  flex-direction: column;
}

.is-cancelled {
  opacity: 0.6;
  background: repeating-linear-gradient(45deg, var(--card-bg), var(--card-bg) 10px, #2a2a2a 10px, #2a2a2a 20px) !important;
}

.is-cancelled .rez-title {
  text-decoration: line-through;
}
.rez-title {
  font-weight: 600;
  color: var(--text-primary);
}
.rez-time {
  font-size: 0.9rem;
  color: var(--text-secondary);
}
.no-reservations-placeholder {
  text-align: center;
  padding: 30px;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 16px;
  border: 2px dashed var(--border);
}
.placeholder-icon {
  font-size: 3rem !important;
  color: var(--text-secondary) !important;
  margin-bottom: 12px;
}
.no-reservations-placeholder p {
  color: var(--text-secondary);
  font-weight: 500;
}

/* --- HEADER --- */
.header-section {
  margin-bottom: 40px;
}
.headline {
  font-size: clamp(2rem, 6vw, 2.5rem);
  font-weight: 1000;
  text-transform: uppercase;
  font-style: italic;
  color: var(--text-primary);
}
.sub-headline {
  font-size: 1.1rem;
  color: var(--text-secondary);
}

/* --- TYPE FEED --- */
.type-feed {
  display: grid;
  gap: 20px;
  max-width: 600px;
  margin: auto;
}
.type-card {
  background: linear-gradient(145deg, #2a2a2a, #1a1a1a);
  border-radius: 20px !important;
  border: 1px solid var(--border);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.type-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
}

.card-content {
  padding: 24px;
  flex-grow: 1;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.card-title {
  font-size: 1.4rem;
  font-weight: 1000;
  text-transform: uppercase;
  font-style: italic;
  color: var(--text-primary);
}

.duration-chip {
  background-color: rgba(255, 255, 255, 0.05) !important;
  color: var(--text-secondary) !important;
  font-weight: 500;
}

.card-description {
  font-size: 1rem;
  color: var(--text-secondary);
  line-height: 1.6;
}

.card-action {
  background-color: var(--v-primary);
  color: var(--text-primary);
  padding: 16px 24px;
  text-align: center;
  font-weight: 700;
  font-size: 1rem;
  transition: background-color 0.2s ease;
  display: flex;
  justify-content: center;
  align-items: center;
}

.type-card:hover .card-action {
  background-color: var(--v-primary);
}

.card-action .v-icon {
  transition: transform 0.3s ease;
}

.type-card:hover .card-action .v-icon {
  transform: translateX(5px);
}
</style>
